<template>
  <div class="body">
    <CustomFilterBar
      v-if="!isMobile"
      ref="filterBarPc"
      class="filter"
      :filters="filters"
      v-model="params"
      :collection-address="params.nftContract"
    />
    <b-sidebar id="sidebar-filter" no-header>
      <b-icon
        v-b-toggle.sidebar-filter
        class="arrow-left"
        icon="arrow-left"
        aria-hidden="true"
      />
      <CustomFilterBar
        v-if="isMobile"
        ref="filterBarMobile"
        :filters="filters"
        v-model="params"
        :collection-address="params.nftContract"
      />
    </b-sidebar>

    <div class="market-items-wrapper">
      <div class="game-market-banner">
        <slot name="banner-game" />
      </div>
      <div class="market-overview">
        <slot name="game-statistics" />
      </div>
      <div class="search-sort-bar">
        <div class="input-wrapper">
          <SearchInput v-model="params.search" :placeholder="'Search'" />
          <CustomDropdown
            v-if="collections.length > 0"
            v-model="params.nftContract"
            class="custom-dropdown collection"
            :display-list="collectionNames"
            :value-list="collectionAddresses"
          />
          <CustomDropdown
            v-model="params.sortByPrice"
            class="custom-dropdown sort"
            :none-value-display="'Sort by'"
            :display-list="['Price ↑', 'Price ↓']"
            :value-list="[1, -1]"
          />
          <slot name="extend-searching-bar" />
        </div>
        <SecondaryButton class="clear-btn" @onClick="clearFilter">
          Clear All
        </SecondaryButton>
        <primary-button v-b-toggle.sidebar-filter class="filter-btn">
          Filter
        </primary-button>
      </div>
      <div v-if="rows.length > 0" class="artwork-grid">
        <slot
          v-for="(row, index) in rows"
          name="grid-data"
          :index="index"
          :item="row"
        />
      </div>
      <div v-else class="blank-state">
        No items found
      </div>
      <Pagination
        v-if="lastPage > 1"
        ref="paginate"
        class="market-pagination"
        :page-count="lastPage"
        :page="perPage"
        @handlePageChange="handleChangePage"
      />
    </div>
  </div>
</template>

<script>
import CustomFilterBar from '@/components/BaseComponents/CustomFilterBar.vue';
import SearchInput from '@/components/BaseComponents/SearchInput.vue';
import CustomDropdown from '@/components/BaseComponents/CustomDropdown.vue';
import SecondaryButton from '@/components/BaseComponents/SecondaryButton.vue';
import Pagination from '@/components/BaseComponents/CustomPagination.vue';
import PrimaryButton from '@/components/BaseComponents/PrimaryButton.vue';
import config from '@/config';
import {
  getFinalDutchAuctionPrices,
  getFinalFixPrices
} from '@/helpers/market-prices';

export default {
  name: 'MarketBase',
  components: {
    CustomFilterBar,
    SearchInput,
    CustomDropdown,
    SecondaryButton,
    Pagination,
    PrimaryButton
  },
  props: {
    getData: {
      type: Function,
      require: true
    },
    chunk: {
      type: Number,
      default: 10
    },
    collections: {
      type: Array,
      default: () => []
    },
    filters: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      totalRecord: 0,
      lastPage: 0,
      page: 1,
      perPage: 12,
      fetching: false,
      rows: [],
      params: {
        nftContract: '',
        sortByPrice: '',
        sellMethod: '',
        minPrice: '',
        maxPrice: '',
        search: '',
        currency: config.defaultCurrency.address,
        attributes: []
      },
      emptyData: false,
      marketPlaceContracts: {
        commonMarketPlace: '',
        auctionMarketPlace: ''
      },
      isMobile: false
    };
  },
  computed: {
    collectionNames() {
      if (!this.collections) {
        return [];
      }
      return this.collections.map(collection => {
        return collection.nftName;
      });
    },
    collectionAddresses() {
      if (!this.collections) {
        return [];
      }
      return this.collections.map(collection => {
        return collection.nftContract;
      });
    }
  },
  watch: {
    params: {
      handler() {
        this.page = 1;
        this.fetch();
      },
      deep: true
    },
    'params.nftContract'() {
      this.$emit('set-filtered-collection-address', this.params.nftContract);
    },
    page() {
      if (this.$refs && this.$refs.paginate) {
        this.$refs.paginate.page = this.page;
      }
    }
  },
  mounted() {
    this.isMobile = window.isMobile;
    this.$nextTick(() => {
      this.fetch();
    });
    this.$on('DataTable:filter', params => {
      this.filter(params);
    });
  },
  methods: {
    handleChangePage(pageNum) {
      this.page = pageNum;
      this.fetch();
    },
    clearFilter() {
      this.$emit('clearExtendFilter');
      this.$nextTick(() => {
        this.$set(this.params, 'nftContract', this.collectionAddresses[0]);
        this.$set(this.params, 'sortByPrice', '');
        this.$set(this.params, 'sellMethod', '');
        this.$set(this.params, 'minPrice', null);
        this.$set(this.params, 'maxPrice', null);
        this.$set(this.params, 'search', '');
        this.$set(this.params, 'currency', config.defaultCurrency.address);
        this.$refs.filterBarPc && this.$refs.filterBarPc.resetGenres();
        this.$refs.filterBarMobile && this.$refs.filterBarMobile.resetGenres();
        console.log('============== clearFilter', JSON.stringify(this.params));
      });
    },

    async fetch() {
      const meta = {
        page: this.page,
        limit: this.perPage
      };

      this.fetching = true;
      const paramsData = Object.assign(meta, this.params);
      const res = await this.getData(paramsData);
      const docs = (res && res.docs) || [];
      if (!res) {
        return;
      }

      if (!docs) {
        this.rows = res;
        this.emptyData = window._.isEmpty(this.rows);
        this.$emit('DataTable:finish');
        this.fetching = false;
        return;
      }
      this.page = parseInt(res.page);
      this.totalPage = parseInt(res.totalDocs);
      this.lastPage = parseInt(res.totalPages);
      this.perPage = parseInt(res.limit);
      this.rows = docs;

      this.emptyData = window._.isEmpty(this.rows);
      this.$emit('DataTable:finish');
      this.fetching = false;

      await this.fetchNftPrices();
    },
    refresh() {
      this.page = 1;
      this.params = {};
      this.fetch();
    },
    filter(params) {
      this.page = 1;
      this.params = params;
      this.fetch();
    },
    setPage(pageNumber) {
      this.page = pageNumber;
      if (!this.$refs && !!this.$refs.paginate) {
        this.$refs.paginate.setPage(pageNumber);
      }
      this.fetch();
    },
    async fetchNftPrices() {
      const nftContract = this.params.nftContract;

      const fixedPriceNfts = this.rows.filter(
        nft => nft.sellMethod === 'fixed_price'
      );
      const fixPrices =
        (await getFinalFixPrices(
          this.marketPlaceContracts.commonMarketPlace,
          nftContract,
          fixedPriceNfts.map(nft => nft.nftId)
        )) || [];

      const auctionNfts = this.rows.filter(
        nft => nft.sellMethod === 'dutch_auction'
      );
      const auctionPrices =
        (await getFinalDutchAuctionPrices(
          this.marketPlaceContracts.auctionMarketPlace,
          nftContract,
          auctionNfts.map(nft => nft.nftId)
        )) || [];

      this.rows.forEach(row => {
        if (row.sellMethod === 'fixed_price') {
          const fixPrice =
            fixPrices.find(
              fixPrice => Number(fixPrice.nftId) === Number(row.nftId)
            ) || {};
          this.$set(row, 'price', fixPrice.price);
          this.$set(row, 'currency', fixPrice.currency);
        }
        if (row.sellMethod === 'dutch_auction') {
          const auctionPrice =
            auctionPrices.find(
              auctionPrice => Number(auctionPrice.nftId) === Number(row.nftId)
            ) || {};
          this.$set(row, 'price', auctionPrice.price);
          this.$set(row, 'currency', auctionPrice.currency);
        }
      });
    },
    setupMarketPlaceContract({ commonMarketPlace, auctionMarketPlace }) {
      this.marketPlaceContracts = {
        commonMarketPlace,
        auctionMarketPlace
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/config/_variables';
.sidebar {
  display: none;
}
.filter-btn {
  display: none;
}
.body {
  margin: 40px auto;
  display: flex;
  flex-wrap: wrap;

  .arrow-left {
    position: absolute;
    right: 30px;
    top: 35px;
  }
  .market-items-wrapper {
    width: calc(100% - 30px - 242px);
    margin-left: 30px;
    .game-market-banner {
      width: -webkit-fill-available;
    }
    .market-overview {
      margin: 30px auto 0;
    }

    .search-sort-bar {
      margin-top: 30px;
      display: flex;
      justify-content: space-between;
      .input-wrapper {
        display: flex;
        .custom-dropdown {
          min-width: 160px;
        }
        > div {
          margin-right: 10px;
        }
      }
    }
    .blank-state {
      text-align: center;
      margin: 30px 0;
      font-size: 2rem;
      height: 250px;
      border: 1px solid #e5e8eb;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      .clear-btn {
        margin-top: 10px;
      }
    }
    .artwork-grid {
      margin-top: 20px;
      display: grid;
      grid-template-areas: 'a a a a';
      grid-auto-columns: 25%;
      grid-row-gap: 16px;
      .grid-item-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
    .market-pagination {
      margin-top: 26px;
      display: flex;
      justify-content: flex-end;
    }
  }
}
@media (max-width: $breakpoint-desktop) {
  .filter-btn {
    display: block;
    max-height: 48px;
    margin-left: 16px;
  }
  .body {
    .filter {
      display: none;
    }
    .market-items-wrapper {
      width: 100%;
      margin: auto;
      .search-sort-bar {
        .input-wrapper {
          flex-wrap: wrap;
          div {
            margin: 8px 0;
          }
        }

        .clear-btn {
          white-space: nowrap;
          max-height: 48px;
        }
      }
      .artwork-grid {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
      }
    }
  }
  .sidebar {
    display: block;
    position: absolute;
    bottom: 50px;
    left: 0;
    right: 0;
    z-index: 1040;
    margin: auto;
  }
}
@media (max-width: $breakpoint-macbook) {
  .body {
    .market-items-wrapper {
      .artwork-grid {
        grid-template-areas: 'a a a a';
        grid-auto-columns: 25%;
      }
    }
  }
}
@media (max-width: $breakpoint-mobile) {
  .filter-btn {
    margin: 8px;
  }
  .body {
    .market-items-wrapper {
      .market-overview {
        flex-wrap: wrap;
        width: 100%;
        justify-content: center;
        ::v-deep .section {
          width: 33%;
          text-align: center;
        }
      }
      .search-sort-bar {
        flex-wrap: wrap;
        justify-content: center;
        .input-wrapper {
          flex-wrap: wrap;
          ::v-deep .input-search-wrapper {
            width: 100%;
            input {
              width: 100%;
            }
          }
          .sort,
          .collection {
            width: 100%;
          }
          div {
            margin: 8px;
          }
        }
        .clear-btn {
          margin: 8px;
        }
      }
      .artwork-grid {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
      }
      .custom-pagination {
        justify-content: center;
      }
    }
  }
}
</style>
